import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  merge,
  NEVER,
  type Observable,
  Subject,
  share,
  shareReplay,
  skip,
  startWith,
  switchMap,
  takeUntil,
  tap,
  timer,
  withLatestFrom,
} from "rxjs"
import { HTML_PREFIX_SCROLL_NAVIGATOR } from "../../constants"
import type { Context } from "../../context/Context"
import type { HookManager } from "../../hooks/HookManager"
import type { ReaderSettingsManager } from "../../settings/ReaderSettingsManager"
import type { Spine } from "../../spine/Spine"
import { type SpinePosition, UnboundSpinePosition } from "../../spine/types"
import { AbstractPosition } from "../../types"
import { setStylePropertyIfChanged } from "../../utils/dom"
import { isDefined } from "../../utils/isDefined"
import { ReactiveEntity } from "../../utils/ReactiveEntity"
import { observeResize, watchKeys } from "../../utils/rxjs"
import type { Viewport } from "../../viewport/Viewport"
import type {
  NavigationModeController,
  NavigationModeControllerNavigationEntry,
} from "../types"

export class ScrollPosition extends AbstractPosition {}
export class UnboundScrollPosition extends AbstractPosition {
  public readonly __symbol = Symbol(`UnboundScrollPosition`)
}

export type ScrollNavigationEntry = NavigationModeControllerNavigationEntry

export class ScrollNavigationController
  extends ReactiveEntity<{
    element: HTMLElement | undefined
  }>
  implements NavigationModeController
{
  protected navigateSubject = new Subject<ScrollNavigationEntry>()
  protected scrollingSubject = new BehaviorSubject(false)

  public isNavigating$: Observable<boolean>
  public userScroll$: Observable<Event>

  constructor(
    protected viewport: Viewport,
    protected settings: ReaderSettingsManager,
    protected hookManager: HookManager,
    protected spine: Spine,
    protected context: Context,
  ) {
    super({
      element: undefined,
    })

    const elementCreation$ = this.context.pipe(
      watchKeys(["rootElement"]),
      tap(({ rootElement }) => {
        if (!rootElement) return

        const element = this.context.document.createElement(`div`)
        element.setAttribute(`data-${HTML_PREFIX_SCROLL_NAVIGATOR}`, "")
        element.appendChild(this.viewport.value.element)
        rootElement.appendChild(element)

        this.update({ element })
      }),
    )

    const toggleElementDisplay$ = combineLatest([
      settings.watch([`computedPageTurnMode`]),
      this.watch("element"),
    ]).pipe(
      tap(([{ computedPageTurnMode }, element]) => {
        if (!element) return

        if (computedPageTurnMode === `scrollable`) {
          setStylePropertyIfChanged(element.style, `display`, `block`)
        } else {
          setStylePropertyIfChanged(element.style, `display`, `contents`)
        }
      }),
    )

    const updateScrollPositionOnNavigation$ = this.navigateSubject.pipe(
      tap(this.applyNavigationPosition),
    )

    this.isNavigating$ = this.scrollingSubject.asObservable()

    // might be a bit overkill but we want to be sure of sure
    const isSpineScrolling$ = merge(
      spine.element$.pipe(
        filter(isDefined),
        switchMap((element) => observeResize(element)),
      ),
      spine.element$.pipe(
        filter(isDefined),
        switchMap((element) => fromEvent(element, "scroll")),
      ),
      spine.spineItemsObserver.itemResize$,
    ).pipe(
      switchMap(() =>
        timer(10).pipe(
          map(() => false),
          startWith(true),
        ),
      ),
      distinctUntilChanged(),
      startWith(false),
    )

    const scrollHappeningFromBrowser$ = combineLatest([
      isSpineScrolling$,
      this.isNavigating$,
    ]).pipe(
      map(
        ([spineScrolling, viewportScrolling]) =>
          spineScrolling || viewportScrolling,
      ),
      shareReplay(1),
    )

    this.userScroll$ = this.watch("element").pipe(
      filter(isDefined),
      switchMap((element) =>
        settings.watch(["computedPageTurnMode"]).pipe(
          switchMap(({ computedPageTurnMode }) =>
            computedPageTurnMode === "controlled"
              ? NEVER
              : fromEvent(element, `scroll`).pipe(
                  withLatestFrom(scrollHappeningFromBrowser$),
                  filter(
                    ([, shouldAvoidScrollEvent]) => !shouldAvoidScrollEvent,
                  ),
                  map(([event]) => event),
                ),
          ),
        ),
      ),
      share(),
    )

    merge(
      elementCreation$,
      toggleElementDisplay$,
      updateScrollPositionOnNavigation$,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe()
  }

  /**
   * Authoritative DOM-scroll dedup: this controller owns the surface and
   * is the only place that can compare against the actual scaled scroll
   * target. Upstream same-`SpinePosition` dedup is unsafe because the same
   * spine position maps to different DOM scroll targets after a scale
   * change, layout reflow, or external scroll drift.
   *
   * @see https://stackoverflow.com/questions/22111256/translate3d-vs-translate-performance
   * for remark about flicker / fonts smoothing
   */
  protected applyNavigationPosition = ({ position }: ScrollNavigationEntry) => {
    const element = this.value.element

    if (!element) return

    const scaledPosition = this.fromSpinePosition(position)

    if (
      Math.round(scaledPosition.x) === element.scrollLeft &&
      Math.round(scaledPosition.y) === element.scrollTop
    ) {
      return
    }

    this.scrollingSubject.next(true)

    element.scrollTo({
      left: scaledPosition.x,
      top: scaledPosition.y,
      behavior: "instant",
    })

    timer(1)
      .pipe(
        tap(() => {
          this.scrollingSubject.next(false)
        }),
        takeUntil(merge(this.scrollingSubject.pipe(skip(1)), this.destroy$)),
      )
      .subscribe()

    this.hookManager.execute("onViewportOffsetAdjust", undefined)
  }

  public update(
    value: Partial<{
      element: HTMLElement | undefined
    }>,
  ) {
    this.mergeCompare(value)
  }

  public destroy() {
    super.destroy()
    this.value.element?.remove()
  }

  navigate(navigation: NavigationModeControllerNavigationEntry) {
    this.navigateSubject.next(navigation)
  }

  public isActive = () => {
    return this.settings.values.computedPageTurnMode === "scrollable"
  }

  public getNavigationVisibleArea = () => this.viewport.relativeViewport

  public fromScrollPosition(position: UnboundScrollPosition | ScrollPosition) {
    const scaleFactor = this.viewport.scaleFactor

    return new UnboundSpinePosition({
      x: position.x / scaleFactor,
      y: position.y / scaleFactor,
    })
  }

  public fromSpinePosition(position: UnboundSpinePosition | SpinePosition) {
    const scaleFactor = this.viewport.scaleFactor

    return new ScrollPosition({
      x: position.x * scaleFactor,
      y: position.y * scaleFactor,
    })
  }

  public get scrollPosition() {
    const element = this.value.element

    return new ScrollPosition({
      x: element?.scrollLeft ?? 0,
      y: element?.scrollTop ?? 0,
    })
  }
}
